import * as yup from "yup";
import { validateCNPJ } from "../utils/cnpj.util.ts";
import { validateCPF } from "../utils/cpf.util.ts";
import { brDateToIso } from "../utils/date.util.ts";

export const validationSchema = yup.object().shape({
  nomeEmpresa: yup.string().required("Nome da empresa é obrigatório"),
  cnpj: yup.string().test("log-cnpj", "CNPJ deve ser válido", (value) => {
    if (!value) return false;
    const cleanValue = value.replace(/[^\d]+/g, "");
    return validateCNPJ(cleanValue);
  }),
  cpf: yup.string().test("log-cpf", "CPF deve ser válido", (value) => {
    if (!value) return false;
    const cleanValue = value.replace(/[^\d]+/g, "");
    return validateCPF(cleanValue);
  }),
  emailEmpresa: yup
    .string()
    .email("E-mail inválido")
    .required("E-mail da empresa é obrigatório"),
  telefone: yup
    .string()
    .required("Telefone é obrigatório")
    .matches(
      /^\(\d{2}\) \d{5}-\d{4}$/,
      "Formato de telefone inválido. Utilize (XX) XXXXX-XXXX."
    ),
  email: yup.string().email("E-mail inválido").required("E-mail é obrigatório"),
  senha: yup
    .string()
    .min(6, "A senha deve ter pelo menos 6 caracteres")
    .required("Senha obrigatória"),
  nome: yup.string().required("Nome do médico é obrigatório"),
  crm: yup.string().required("CRM é obrigatório"),
  empresa: yup.number().default(1),
  endereco: yup.object().shape({
    cep: yup.string().required("CEP é obrigatório"),
    bairro: yup.string().required("Bairro é obrigatório"),
    cidade: yup.string().required("Cidade é obrigatória"),
    uf: yup.string().required("UF é obrigatória"),
    rua: yup.string().required("Rua é obrigatória"),
    numero: yup.string().required("Número é obrigatório"),
  }),
});

export const validationSchemaRegistration = yup.object().shape({
  nomeEmpresa: yup.string().required("Nome da empresa é obrigatório"),
  cnpj: yup.string().test("log-cnpj", "CNPJ deve ser válido", (value) => {
    if (!value) return false;
    const cleanValue = value.replace(/[^\d]+/g, "");
    return validateCNPJ(cleanValue);
  }),
  emailEmpresa: yup
    .string()
    .email("E-mail inválido")
    .required("E-mail da empresa é obrigatório"),
  telefone: yup
    .string()
    .required("Telefone é obrigatório")
    .matches(
      /^\(\d{2}\) \d{5}-\d{4}$/,
      "Formato de telefone inválido. Utilize (XX) XXXXX-XXXX."
    ),
  email: yup.string().email("E-mail inválido").required("E-mail é obrigatório"),
  senha: yup
    .string()
    .min(6, "A senha deve ter pelo menos 6 caracteres")
    .required("Senha obrigatória"),
    
});

export const validationMedicoSchema = yup.object().shape({
  telefone: yup
    .string()
    .required("Telefone é obrigatório")
    .matches(
      /^\(\d{2}\) \d{5}-\d{4}$/,
      "Formato de telefone inválido. Utilize (XX) XXXXX-XXXX."
    ),
  email: yup.string().nullable().notRequired(),
  nome: yup
    .string()
    .required("Nome do médico é obrigatório"),
  crm: yup.string().required("CRM do médico é obrigatório"),
  endereco: yup.object().shape({
    cep: yup.string(),
    bairro: yup.string(),
    cidade: yup.string(),
    uf: yup.string(),
    rua: yup.string(),
    numero: yup.string(),
  }),
});

export const validationPatientSchema = yup.object().shape({
  cpf: yup
    .string()
    .nullable()
    .notRequired()
    .test("log-cpf", "CPF deve ser válido", (value) => {
      if (!value || value.trim() === "") return true;
      const cleanValue = value.replace(/[^\d]+/g, "");
      return validateCPF(cleanValue);
    }),
  telefone: yup
    .string()
    .nullable()
    .notRequired()
    .test(
      "telefone-valido",
      "Formato de telefone inválido. Utilize (XX) XXXXX-XXXX.",
      (value) => {
        if (!value || value.trim() === "") return true;
        return /^\(\d{2}\) \d{5}-\d{4}$/.test(value);
      }
    ),
  email: yup.string().nullable().notRequired().email("E-mail inválido"),
  nome: yup.string().required("Nome do paciente é obrigatório"),
  dataNascimento: yup
    .string()
    .nullable()
    .notRequired()
    .test("data-nascimento-valida", "Data de nascimento inválida", (value) => {
      if (!value) return true;
      return !!brDateToIso(value);
    })
    .test("data-nascimento-passado", "Data de nascimento não pode ser no futuro", (value) => {
      if (!value) return true;
      const iso = brDateToIso(value);
      if (!iso) return true; // já barrado pelo teste anterior
      return new Date(iso) <= new Date();
    }),
  endereco: yup.object().shape({
    cep: yup.string(),
    bairro: yup.string(),
    cidade: yup.string(),
    uf: yup.string(),
    rua: yup.string(),
    numero: yup.string(),
  }),
});

export const validationInstrumentadorSchema = yup.object().shape({
  telefone: yup
    .string()
    .required("Telefone é obrigatório")
    .matches(
      /^\(\d{2}\) \d{5}-\d{4}$/,
      "Formato de telefone inválido. Utilize (XX) XXXXX-XXXX."
    ),
  cpf: yup.string().test("log-cpf", "CPF deve ser válido", (value) => {
    if (!value) return false;
    const cleanValue = value.replace(/[^\d]+/g, "");
    return validateCPF(cleanValue);
  }),
  email: yup.string().nullable().notRequired().email("E-mail inválido"),
  nome: yup.string().required("Nome do instrumentador é obrigatório"),
  endereco: yup.object().shape({
    cep: yup.string(),
    bairro: yup.string(),
    cidade: yup.string(),
    uf: yup.string(),
    rua: yup.string(),
    numero: yup.string(),
  }),
  notificarEmail: yup.boolean().default(true),
});

export const validationHospitalSchema = yup.object().shape({
  nome: yup.string().required("Nome do hospital é obrigatório"),
  cnpj: yup
    .string()
    .required("CNPJ é obrigatório")
    .test("log-cnpj", "CNPJ deve ser válido", (value) => {
      if (!value) return false;
      const cleanValue = value.replace(/[^\d]+/g, "");
      return validateCNPJ(cleanValue);
    }),
});

export const validationTipoCirurgiaSchema = yup.object().shape({
  nome: yup.string().required("Nome do tipo de cirurgia é obrigatório"),
  observacao: yup.string().nullable().optional(),
});

export const validationHospitalUpdateSchema = yup.object().shape({
  nome: yup.string().required("Nome do hospital é obrigatório"),
  cnpj: yup
    .string()
    .required("CNPJ é obrigatório")
    .test("log-cnpj", "CNPJ deve ser válido", (value) => {
      if (!value) return false;
      const cleanValue = value.replace(/[^\d]+/g, "");
      return validateCNPJ(cleanValue);
    }),
});

export const validationConvenioSchema = yup.object().shape({
  nome: yup.string().required("Nome do convenio é obrigatório"),
  cnpj: yup
    .string()
    .required("CNPJ é obrigatório")
    .test("log-cnpj", "CNPJ deve ser válido", (value) => {
      if (!value) return false;
      const cleanValue = value.replace(/[^\d]+/g, "");
      return validateCNPJ(cleanValue);
    }),
});

export const validationConvenioUpdateSchema = yup.object().shape({
  nome: yup.string().required("Nome do convenio é obrigatório"),
  cnpj: yup
    .string()
    .required("CNPJ é obrigatório")
    .test("log-cnpj", "CNPJ deve ser válido", (value) => {
      if (!value) return false;
      const cleanValue = value.replace(/[^\d]+/g, "");
      return validateCNPJ(cleanValue);
    }),
});

// "nome" e opcional de proposito: tornar obrigatorio quebraria quem ja cadastra
// usuario so com e-mail, e o backend aceita nulo desde sempre.
export const validationUsuarioSchema = yup.object().shape({
  nome: yup.string().nullable().notRequired(),
  email: yup.string().email("E-mail inválido").required("E-mail é obrigatório"),
  senha: yup.string().min(6, "A senha deve ter pelo menos 6 caracteres").required("Senha é obrigatória"),
  telefone: yup
    .string()
    .required("Telefone é obrigatório")
    .matches(/^\(\d{2}\) \d{5}-\d{4}$/, "Formato de telefone inválido. Utilize (XX) XXXXX-XXXX."),
});

// Sem "email" — não é editável (é a credencial de login). "senha" fica
// Sem campo de senha: PATCH /usuario/alterar nao troca mais senha de ninguem.
// Cada um muda a propria em Configuracoes > Segurança (exigindo a atual), ou
// pelo link de recuperacao por e-mail.
export const validationUsuarioUpdateSchema = yup.object().shape({
  nome: yup.string().nullable().notRequired(),
  telefone: yup
    .string()
    .required("Telefone é obrigatório")
    .matches(/^\(\d{2}\) \d{5}-\d{4}$/, "Formato de telefone inválido. Utilize (XX) XXXXX-XXXX."),
});

export const validationCirurgiaSchema = yup.object({
  medico: yup.string().required("Campo obrigatório"),
  paciente: yup.string().required("Campo obrigatório"),
  hospital: yup.string().required("Campo obrigatório"),
  instrumentador1: yup.string().required("Instrumentador 1 é obrigatório"),
  instrumentador2: yup.string().nullable().optional(),
  instrumentador3: yup.string().nullable().optional(),
  tipoCirurgia: yup.string().required("Campo obrigatório"),
  convenio: yup.string().nullable().optional(),
  observacao: yup.string().nullable().optional(),
  data: yup.string().required("Campo obrigatório"),
});
